import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus,
  Send,
  Search,
  Archive,
  Pencil,
  MessageSquare,
  Check,
  Loader2,
  ArrowLeft,
  Paperclip,
  FileText,
  Download,
  X,
  Wand2,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { notifyRemembered } from "@/lib/memoryToast";
import { IntakeResumeBanner } from "@/components/IntakeResumeBanner";
import { CreateDocDialog } from "@/components/tools/CreateDocDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SargeMarkdown } from "@/components/SargeMarkdown";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function downloadArtifact(id, filename) {
  const a = document.createElement("a");
  a.href = `${BACKEND}/api/tools/artifacts/${id}/download`;
  a.download = filename || "";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function ConversationList({ activeId, onOpen, refreshKey }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [archived, setArchived] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations", { params: { archived, q: q || undefined } });
      setItems(data);
    } catch (e) {
      /* ignore */
    }
  }, [archived, q]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-wr-border space-y-2">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-wr-muted" />
          <Input
            data-testid="conv-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search conversations"
            className="pl-9 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0 h-9 text-sm"
          />
        </div>
        <button
          onClick={() => setArchived((a) => !a)}
          data-testid="conv-toggle-archived"
          className="wr-label hover:text-white flex items-center gap-1.5"
        >
          <Archive size={12} /> {archived ? "Showing archived" : "Show archived"}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {items.length === 0 && (
          <p className="wr-label p-4 text-wr-muted">No conversations.</p>
        )}
        {items.map((c) => (
          <button
            key={c.id}
            onClick={() => onOpen(c.id)}
            data-testid={`conv-item-${c.id}`}
            className={`w-full text-left px-4 py-3 border-b border-wr-border transition-colors ${
              activeId === c.id ? "bg-wr-redMuted border-l-2 border-l-wr-red" : "hover:bg-wr-surface2"
            }`}
          >
            <p className="text-sm text-white truncate">{c.title}</p>
            <p className="wr-mono text-[0.68rem] text-wr-muted mt-0.5">
              {new Date(c.updated_at).toLocaleDateString()}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ m }) {
  const isUser = m.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}`}>
      <div className={`max-w-[85%] sm:max-w-[75%] ${isUser ? "items-end" : "items-start"}`}>
        <div
          className={`px-4 py-3 border ${
            isUser
              ? "bg-wr-surface2 border-wr-border text-white"
              : "bg-wr-surface border-l-2 border-l-wr-red border-y-wr-border border-r-wr-border text-zinc-100"
          }`}
        >
          {!isUser && <span className="wr-label !text-wr-red block mb-2">▸ SARGE</span>}
          {isUser ? (
            <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{m.content}</p>
          ) : (
            <SargeMarkdown>{m.content}</SargeMarkdown>
          )}
        </div>
        {m.remembered?.length > 0 && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid={`remembered-notice-${m.id}`}>
            <span className="wr-label !text-emerald-400 flex items-center gap-1"><Check size={12} /> Remembered</span>
            {m.remembered.slice(0, 2).map((r, i) => (
              <span key={i} className="wr-mono text-[0.62rem] text-wr-muted truncate max-w-[220px]">· {r.statement}</span>
            ))}
          </div>
        )}
        {m.tool?.type === "analysis" && (
          <div className="mt-1.5 wr-label !text-wr-sub flex items-center gap-1.5" data-testid={`analysis-provenance-${m.id}`}>
            <FileText size={12} className="text-wr-red" /> Analyzed: {m.tool.provenance?.filename}
          </div>
        )}
        {m.tool?.type === "artifact" && (
          <div className="mt-2 border border-wr-border bg-wr-surface2 p-3 flex items-center justify-between gap-3 max-w-[420px]" data-testid={`artifact-card-${m.tool.artifact.id}`}>
            <div className="min-w-0">
              <p className="text-sm text-white truncate flex items-center gap-2"><FileText size={14} className="text-wr-red shrink-0" /> {m.tool.artifact.filename}</p>
              <p className="wr-mono text-[0.62rem] text-wr-muted mt-0.5">{m.tool.artifact.format?.toUpperCase()} · {(m.tool.artifact.size / 1024).toFixed(0)} KB · {m.tool.artifact.tool_id}</p>
            </div>
            <button onClick={() => downloadArtifact(m.tool.artifact.id, m.tool.artifact.filename)} data-testid={`artifact-download-btn-${m.tool.artifact.id}`}
              className="px-3 py-2 border border-wr-border text-wr-sub hover:text-white hover:border-wr-red shrink-0"><Download size={14} /></button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AskSarge() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingConv, setLoadingConv] = useState(false);
  const [listRefresh, setListRefresh] = useState(0);
  const [renaming, setRenaming] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const scrollRef = useRef(null);

  // ---- Tool layer state ----
  const [selectable, setSelectable] = useState([]);
  const [toolMode, setToolMode] = useState("auto"); // auto | analyze_file | create_document
  const [attached, setAttached] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [showDocDialog, setShowDocDialog] = useState(false);
  const uploadAbort = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tools");
        setSelectable(data.selectable || []);
      } catch (e) { /* tools optional */ }
    })();
  }, []);

  const doUpload = useCallback(async (file) => {
    setUploadError(null);
    setUploading(true);
    setUploadPct(0);
    const controller = new AbortController();
    uploadAbort.current = controller;
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/tools/files", form, {
        signal: controller.signal,
        onUploadProgress: (e) => { if (e.total) setUploadPct(Math.round((e.loaded / e.total) * 100)); },
      });
      setAttached(data);
      setToolMode("analyze_file");
      if (!data.readable) toast.warning("Uploaded, but Sarge couldn't read this file's contents.");
      else toast.success("File attached. Ask Sarge about it.");
    } catch (e) {
      if (controller.signal.aborted) toast.message("Upload cancelled.");
      else { setUploadError(apiError(e)); toast.error(apiError(e)); }
    } finally {
      setUploading(false);
      uploadAbort.current = null;
    }
  }, []);

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) doUpload(file);
  };
  const cancelUpload = () => uploadAbort.current?.abort();
  const clearAttachment = () => { setAttached(null); setUploadError(null); };

  const onDocCreated = (artifact) => {
    setMessages((m) => [...m, { id: `art-${Date.now()}`, role: "assistant",
      content: `Created **${artifact.filename}**. It's saved to your workspace and available in Files & Tools.`,
      tool: { type: "artifact", artifact } }]);
  };

  const loadConv = useCallback(async (cid) => {
    setLoadingConv(true);
    try {
      const { data } = await api.get(`/conversations/${cid}`);
      setConv(data.conversation);
      setMessages(data.messages);
    } catch (e) {
      toast.error(apiError(e));
      navigate("/app/sarge");
    } finally {
      setLoadingConv(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (id) loadConv(id);
    else {
      setConv(null);
      setMessages([]);
    }
  }, [id, loadConv]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const newConversation = async () => {
    try {
      const { data } = await api.post("/conversations", {});
      setListRefresh((k) => k + 1);
      navigate(`/app/sarge/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const send = async () => {
    const content = input.trim();
    if ((!content && !attached) || sending) return;
    let cid = id;
    if (!cid) {
      try {
        const { data } = await api.post("/conversations", {});
        cid = data.id;
        navigate(`/app/sarge/${cid}`);
      } catch (e) {
        toast.error(apiError(e));
        return;
      }
    }

    // Analyze File flow — a file is attached (real gpt-5.4 analysis of extracted content).
    if (attached) {
      const question = content;
      const file = attached;
      setInput("");
      setSending(true);
      setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user",
        content: question || `Analyze ${file.filename}` }]);
      try {
        const { data } = await api.post("/tools/analyze", {
          file_id: file.id, question: question || null, conversation_id: cid });
        setMessages((m) => [
          ...m.filter((x) => !String(x.id).startsWith("tmp-")),
          { id: `u-${Date.now()}`, role: "user", content: question || `Analyze ${file.filename}` },
          { id: `a-${Date.now()}`, role: "assistant", content: data.analysis,
            tool: { type: "analysis", provenance: data.provenance } },
        ]);
        clearAttachment();
        setToolMode("auto");
      } catch (e) {
        toast.error(apiError(e));
        setMessages((m) => m.filter((x) => !String(x.id).startsWith("tmp-")));
      } finally {
        setSending(false);
      }
      return;
    }

    if (!content) return;
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content }]);
    try {
      const { data } = await api.post(`/conversations/${cid}/messages`, { content });
      const assistant = { ...data.assistant_message, remembered: data.remembered || [] };
      setMessages((m) => [
        ...m.filter((x) => !String(x.id).startsWith("tmp-")),
        data.user_message,
        assistant,
      ]);
      // Persistence succeeded — subtle, non-blocking confirmation (no extra model call).
      notifyRemembered(data.remembered, navigate);
      setListRefresh((k) => k + 1);
    } catch (e) {
      // Truthful failure — user message is preserved server-side.
      toast.error(apiError(e));
      if (id || cid) loadConv(cid);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const saveTitle = async () => {
    try {
      const { data } = await api.put(`/conversations/${id}`, { title: titleDraft });
      setConv(data);
      setRenaming(false);
      setListRefresh((k) => k + 1);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleArchive = async () => {
    try {
      await api.put(`/conversations/${id}`, { archived: !conv.archived });
      toast.success(conv.archived ? "Unarchived." : "Archived.");
      setListRefresh((k) => k + 1);
      navigate("/app/sarge");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="flex flex-col h-[calc(100dvh-57px)] md:h-screen">
      <IntakeResumeBanner />
      <div className="flex flex-1 min-h-0">
      {/* Conversation list */}
      <div className={`${id ? "hidden md:flex" : "flex"} md:w-72 w-full shrink-0 border-r border-wr-border flex-col bg-wr-header`}>
        <div className="p-3 border-b border-wr-border">
          <Button
            onClick={newConversation}
            data-testid="new-conversation"
            className="w-full rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider text-xs h-10"
          >
            <Plus size={15} className="mr-2" /> New conversation
          </Button>
        </div>
        <ConversationList activeId={id} onOpen={(cid) => navigate(`/app/sarge/${cid}`)} refreshKey={listRefresh} />
      </div>

      {/* Active conversation */}
      <div className={`${id ? "flex" : "hidden md:flex"} flex-1 min-w-0 flex-col`}>
        {!id ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6" data-testid="sarge-empty">
            <MessageSquare className="text-wr-red mb-4" size={30} />
            <h2 className="wr-heading text-white text-xl">Ask Sarge</h2>
            <p className="text-sm text-wr-sub mt-2 max-w-sm">
              Start a conversation. Sarge answers from your stored operating record — grounded, never
              invented.
            </p>
            <Button
              onClick={newConversation}
              data-testid="sarge-empty-new"
              className="mt-6 rounded-none bg-wr-red hover:bg-wr-redHover text-white wr-mono uppercase tracking-wider"
            >
              <Plus size={15} className="mr-2" /> New conversation
            </Button>
          </div>
        ) : (
          <>
            {/* Conversation header */}
            <div className="border-b border-wr-border px-4 py-3 flex items-center gap-3 bg-wr-header">
              <button
                onClick={() => navigate("/app/sarge")}
                data-testid="conv-back"
                className="md:hidden text-wr-sub hover:text-white"
              >
                <ArrowLeft size={18} />
              </button>
              {renaming ? (
                <div className="flex items-center gap-2 flex-1">
                  <Input
                    autoFocus
                    data-testid="rename-input"
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    className="bg-wr-bg border-wr-border rounded-none focus-visible:ring-0 h-9"
                  />
                  <Button onClick={saveTitle} data-testid="rename-save" className="rounded-none bg-wr-red hover:bg-wr-redHover h-9 text-xs wr-mono">
                    Save
                  </Button>
                </div>
              ) : (
                <>
                  <span className="text-sm text-white truncate flex-1" data-testid="conv-title">
                    {conv?.title}
                  </span>
                  <button
                    onClick={() => {
                      setTitleDraft(conv?.title || "");
                      setRenaming(true);
                    }}
                    data-testid="conv-rename"
                    className="text-wr-sub hover:text-white"
                  >
                    <Pencil size={15} />
                  </button>
                  <button onClick={toggleArchive} data-testid="conv-archive" className="text-wr-sub hover:text-wr-red">
                    <Archive size={15} />
                  </button>
                </>
              )}
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="messages-area">
              {loadingConv && <p className="wr-label">Loading…</p>}
              {!loadingConv && messages.length === 0 && (
                <p className="wr-label text-wr-muted">No messages yet. Ask Sarge anything about your operation.</p>
              )}
              {messages.map((m) => (
                <MessageBubble key={m.id} m={m} />
              ))}
              {sending && (
                <div className="flex justify-start" data-testid="sarge-thinking">
                  <div className="px-4 py-3 border border-l-2 border-l-wr-red border-wr-border bg-wr-surface flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin text-wr-red" />
                    <span className="wr-label">Sarge is assessing…</span>
                  </div>
                </div>
              )}
            </div>

            {/* Composer — keyboard-safe */}
            <div className="sticky bottom-16 md:bottom-0 border-t border-wr-border bg-black/95 backdrop-blur-xl p-3 safe-bottom">
              {/* Tool bar */}
              <div className="flex items-center gap-1.5 mb-2 flex-wrap" data-testid="tool-bar">
                <button onClick={() => setToolMode("auto")} data-testid="tool-mode-auto"
                  className={`wr-mono text-[0.62rem] uppercase tracking-wider px-2.5 py-1 border ${toolMode === "auto" ? "border-wr-red bg-wr-redMuted text-white" : "border-wr-border text-wr-sub hover:text-white"}`}>Auto</button>
                {selectable.includes("analyze_file") && (
                  <button onClick={() => { setToolMode("analyze_file"); fileInputRef.current?.click(); }} data-testid="tool-mode-analyze"
                    className={`wr-mono text-[0.62rem] uppercase tracking-wider px-2.5 py-1 border flex items-center gap-1 ${toolMode === "analyze_file" ? "border-wr-red bg-wr-redMuted text-white" : "border-wr-border text-wr-sub hover:text-white"}`}><Paperclip size={11} /> Analyze File</button>
                )}
                {selectable.includes("create_document") && (
                  <button onClick={() => setShowDocDialog(true)} data-testid="tool-mode-document"
                    className="wr-mono text-[0.62rem] uppercase tracking-wider px-2.5 py-1 border border-wr-border text-wr-sub hover:text-white flex items-center gap-1"><Wand2 size={11} /> Create Document</button>
                )}
                <input ref={fileInputRef} type="file" accept=".pdf,.docx,.xlsx,.csv" onChange={onPickFile} className="hidden" data-testid="file-input" />
                <button onClick={() => fileInputRef.current?.click()} data-testid="attach-file"
                  className="ml-auto text-wr-sub hover:text-white p-1"><Paperclip size={15} /></button>
              </div>

              {/* Upload progress */}
              {uploading && (
                <div className="mb-2 border border-wr-border bg-wr-surface2 p-2.5 flex items-center gap-3" data-testid="upload-progress">
                  <Loader2 size={14} className="animate-spin text-wr-red shrink-0" />
                  <div className="flex-1 h-1.5 bg-wr-bg overflow-hidden"><div className="h-full bg-wr-red transition-all" style={{ width: `${uploadPct}%` }} /></div>
                  <span className="wr-mono text-[0.62rem] text-wr-muted">{uploadPct}%</span>
                  <button onClick={cancelUpload} data-testid="upload-cancel" className="text-wr-sub hover:text-wr-red"><X size={14} /></button>
                </div>
              )}
              {uploadError && !uploading && (
                <div className="mb-2 border border-wr-red/40 bg-wr-redMuted p-2.5 flex items-center justify-between gap-3" data-testid="upload-error">
                  <span className="wr-mono text-[0.62rem] text-wr-red truncate">{uploadError}</span>
                  <button onClick={() => fileInputRef.current?.click()} data-testid="upload-retry" className="wr-mono text-[0.6rem] uppercase text-wr-sub hover:text-white border border-wr-border px-2 py-1">Retry</button>
                </div>
              )}
              {/* Attached file chip */}
              {attached && !uploading && (
                <div className="mb-2 inline-flex items-center gap-2 border border-wr-border bg-wr-surface2 px-2.5 py-1.5" data-testid="file-chip">
                  <FileText size={13} className="text-wr-red" />
                  <span className="text-xs text-white truncate max-w-[220px]">{attached.filename}</span>
                  <span className="wr-mono text-[0.58rem] text-wr-muted uppercase">{attached.kind}</span>
                  <button onClick={clearAttachment} data-testid="file-chip-remove" className="text-wr-sub hover:text-wr-red"><X size={13} /></button>
                </div>
              )}

              <div className="flex items-end gap-2">
                <Textarea
                  data-testid="composer-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={attached ? "Ask Sarge about this file…" : "Message Sarge…"}
                  rows={1}
                  className="flex-1 bg-wr-bg border-wr-border rounded-none focus-visible:ring-0 resize-none max-h-40 min-h-[44px]"
                />
                <Button
                  onClick={send}
                  disabled={sending || uploading || (!input.trim() && !attached)}
                  data-testid="composer-send"
                  className="rounded-none bg-wr-red hover:bg-wr-redHover text-white h-11 w-11 p-0 shrink-0"
                >
                  <Send size={17} />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
      </div>
      {showDocDialog && (
        <CreateDocDialog conversationId={id} onCreated={onDocCreated} onClose={() => setShowDocDialog(false)} />
      )}
    </div>
  );
}
